export type PlanTier = "free" | "pro";

export type PlanLimits = {
  maxLists: number | null;
  maxTasks: number | null;
  allowsAlerts: boolean;
  allowsAnalytics: boolean;
};

export const PRO_PRICE_TEXT = "US$1.99";

/** ISO code for the price above. Stated in the UI so the amount is unambiguous. */
export const PRO_PRICE_CURRENCY = "USD";

/**
 * Length of the introductory free trial.
 *
 * Not configured anywhere yet — the RevenueCat Test Store cannot hold a trial,
 * and a real one is an introductory offer created in App Store Connect and Play
 * Console. Until then this is the length the copy promises, not one anybody
 * receives. See REVENUECAT_SETUP.md.
 */
export const PRO_TRIAL_DAYS = 14;

/**
 * Mirrors the `plan_limits` table. The database is the real gate; this copy
 * exists so an offline client can disable the button before the write fails.
 */
export const PLAN_LIMITS: Record<PlanTier, PlanLimits> = {
  free: {
    maxLists: 1,
    maxTasks: 10,
    allowsAlerts: false,
    allowsAnalytics: false,
  },
  pro: {
    maxLists: null,
    maxTasks: null,
    allowsAlerts: true,
    allowsAnalytics: true,
  },
};

export type Allowance = {
  allowed: boolean;
  limit: number | null;
  used: number;
  remaining: number | null;
  message: string | null;
};

function allowance(
  used: number,
  limit: number | null,
  noun: string,
): Allowance {
  if (limit === null) {
    return { allowed: true, limit: null, used, remaining: null, message: null };
  }
  const remaining = Math.max(limit - used, 0);
  return {
    allowed: remaining > 0,
    limit,
    used,
    remaining,
    message:
      remaining > 0
        ? null
        : `Free accounts include ${limit} ${limit === 1 ? noun : `${noun}s`}. Upgrade to Pro for unlimited.`,
  };
}

export function listAllowance(tier: PlanTier, used: number): Allowance {
  return allowance(used, PLAN_LIMITS[tier].maxLists, "list");
}

export function taskAllowance(tier: PlanTier, used: number): Allowance {
  return allowance(used, PLAN_LIMITS[tier].maxTasks, "task");
}

export function allowsAlerts(tier: PlanTier) {
  return PLAN_LIMITS[tier].allowsAlerts;
}

export function allowsAnalytics(tier: PlanTier) {
  return PLAN_LIMITS[tier].allowsAnalytics;
}

/**
 * Postgres raises plan-limit violations as P0001 with a human-readable
 * message, so surfacing it directly beats a generic failure toast.
 */
export function planLimitMessage(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  const candidate = error as { code?: string; message?: string };
  if (candidate.code !== "P0001" || !candidate.message) return null;
  return candidate.message.includes("TaskScribe Pro")
    ? candidate.message
    : null;
}
